import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowDown01Icon, ArrowUp01Icon } from "@hugeicons/core-free-icons";
import { useLanguage } from "../contexts/LanguageContext";
import { AUTH_BRIDGE } from "../config";
import { isAuthenticated, clearAuthToken } from "../services/authService";
import DgaSearchBox from "./DgaSearchBox";

type MenuItem = {
  key: string;
  label: string;
  to: string;
  icon: React.ReactNode;
};

const SimpleHeader: React.FC = () => {
  const { language, t, isRTL, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ Wrap header for outside click
  const rootRef = useRef<HTMLDivElement>(null);

  // ------------------------------------------------------------
  // ✅ Translation helper: prevents showing raw keys like "home"
  // ------------------------------------------------------------
  const tt = useCallback(
    (key: string, fallback: string) => {
      const v = t(key);
      return !v || v === key ? fallback : v;
    },
    [t]
  );

  // ------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------
  const [openMosqueMenu, setOpenMosqueMenu] = useState(false);
  const mosqueMenuRef = useRef<HTMLDivElement>(null);

    // ✅ NEW: Dropdown state for Land menu
  const [openLandMenu, setOpenLandMenu] = useState(false);
  const landMenuRef = useRef<HTMLDivElement>(null);

  // ✅ NEW: Dropdown state for Mosque Contribution menu
  const [openContributionMenu, setOpenContributionMenu] = useState(false);
  const contributionMenuRef = useRef<HTMLDivElement>(null);

  const [openMobileMenu, setOpenMobileMenu] = useState(false);
  const mobileMenuId = "mobile-nav-menu";

  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const closeAll = () => {
    setOpenMosqueMenu(false);
    setOpenContributionMenu(false);
    setOpenMobileMenu(false);
    setOpenUserMenu(false);
    setOpenMobileMenu(false);
    setSearchPanelOpen(false);
  };

  const handleLogout = () => {
    clearAuthToken();
    window.location.href = "/";
  };


  const isActivePath = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  // ------------------------------------------------------------
  // Main menu detection
  // ------------------------------------------------------------
  // const pathname = useMemo(() => {
  //   const raw = (location.pathname || "").toLowerCase();
  //   // Some deployments mount the SPA under "/home" (WordPress wrapper).
  //   // Normalize so active menu highlighting remains consistent.
  //   return raw.startsWith("/home/") ? raw.slice("/home".length) : raw;
  // }, [location.pathname]);
  const pathname = useMemo(() => {
    return (location.pathname || "").toLowerCase();
  }, [location.pathname]);


  const isMosqueMapPage = useMemo(() => pathname.startsWith("/mosques/map"), [pathname]);

  const isMosqueSearchPage = useMemo(() => {
    if (pathname.startsWith("/service/mosque-search")) return true;
    if (pathname === "/mosquesearch" || pathname.startsWith("/mosquesearch/")) return true;
    if (pathname.startsWith("/mosques/") && !pathname.startsWith("/mosques/map")) return true;
    return false;
  }, [pathname]);

  const isMosqueSearchActive = isMosqueSearchPage || isMosqueMapPage;
  // ✅ Land service page
  const isLandServicePage = useMemo(() => {
    return pathname === "/service/available-lands";
  }, [pathname]);

  // ✅ Land list page
  const isLandListPage = useMemo(() => {
    return pathname === "/benefaction/lands" || pathname.startsWith("/benefaction/lands/");
  }, [pathname]);

  // ✅ Land map page
  const isLandMapPage = useMemo(() => {
    return pathname === "/benefaction/lands/map" || pathname.startsWith("/benefaction/lands/map/");
  }, [pathname]);

  // ✅ Parent land menu active if any land page is active
  const isLandActive = useMemo(() => {
    return isLandServicePage || isLandListPage || isLandMapPage;
  }, [isLandServicePage, isLandListPage, isLandMapPage]);

  

  // ✅ Donation Opportunities pages (service journey)
  const isDonationOpportunitiesPage = useMemo(() => {
    return (
      pathname === "/mosque/donation-opportunities" ||
      pathname.startsWith("/mosque/donation-opportunities/") ||
      pathname.startsWith("/mosque/donation-details/") ||
      pathname === "/mosque/information-donation" ||
      pathname.startsWith("/mosque/donation-request/")
    );
  }, [pathname]);

  // ✅ NEW: Donation Opportunities Map page
  const isDonationOpportunitiesMapPage = useMemo(() => {
    return pathname === "/mosque/donation-opportunities-map" || pathname.startsWith("/mosque/donation-opportunities-map/");
  }, [pathname]);

  // ✅ Parent menu active (Contribution dropdown)
  const isMosqueContributionActive = isDonationOpportunitiesPage || isDonationOpportunitiesMapPage;

  const isServiceContributionPage = pathname === "/service/mosque-contribution";
  // (Optional) keep old flag if you still use /service/mosque-contribution somewhere
  const isMosqueDonationActive = useMemo(() => {
    return pathname === "/service/mosque-contribution" || isMosqueContributionActive;
  }, [pathname, isMosqueContributionActive]);

  const [loggedIn, setLoggedIn] = useState(() => isAuthenticated());
  const [openUserMenu, setOpenUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Sync header with auth (login/logout in same tab + storage change in other tabs)
  useEffect(() => {
    const syncAuth = () => setLoggedIn(isAuthenticated());
    const eventName = AUTH_BRIDGE?.authChangedEventName ?? "auth:state-changed";
    window.addEventListener(eventName, syncAuth);
    const onStorage = (e: StorageEvent) => {
      if (e.key === "authToken") syncAuth();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(eventName, syncAuth);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // ------------------------------------------------------------
  // Close on outside click / ESC
  // ------------------------------------------------------------
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Click outside header
      if (!rootRef.current?.contains(target)) {
        closeAll();
        return;
      }

      // Click inside header but outside mosque dropdown
      if (mosqueMenuRef.current && !mosqueMenuRef.current.contains(target)) {
        setOpenMosqueMenu(false);
      }
      if (contributionMenuRef.current && !contributionMenuRef.current.contains(target)) {
        setOpenContributionMenu(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setOpenUserMenu(false);
      }
      // ✅ Close land dropdown if click was outside it
      if (landMenuRef.current && !landMenuRef.current.contains(target)) {
        setOpenLandMenu(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeAll();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close mobile menu and search panel when route changes
  useEffect(() => {
    setOpenMobileMenu(false);
    setSearchPanelOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!searchPanelOpen) return;
    const id = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [searchPanelOpen]);

  // ------------------------------------------------------------
  // Submenu item (ONLY Mosque Map Page)
  // ------------------------------------------------------------
  const mosqueMapItem: MenuItem = useMemo(
    () => ({
      key: "mosqueMap",
      label: tt("mosqueMap", "خريطة المساجد"),
      to: "/mosques/map",
      icon: (
        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3z" />
          <path d="M9 3v15" />
          <path d="M15 6v15" />
        </svg>
      ),
    }),
    [tt]
  );

  // ------------------------------------------------------------
  // Styling (unchanged)
  // ------------------------------------------------------------
  const navBase =
    "relative inline-flex items-center gap-2 px-8 py-[16px] leading-none rounded-xl font-semibold transition-all select-none";

  const navInactive = "text-gray-800 hover:bg-gray-100 hover:text-gray-900";
  const navActive = "bg-sa-600 text-white shadow-sm";
  const navUnderline =
    "after:content-[''] after:absolute after:left-8 after:right-8 after:-bottom-2 after:h-[4px] after:rounded-full after:bg-sa-200";

  // ✅ Align submenu text based on RTL/LTR
  const submenuTextAlign = isRTL ? "text-right" : "text-left";

  return (
    <div ref={rootRef}>
      <header className="bg-white shadow-sm sticky top-0 z-[1000]" dir={isRTL ? "rtl" : "ltr"}>
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-nowrap items-center justify-between gap-2 md:gap-6 lg:gap-8 h-auto min-h-[64px] py-2 md:py-0 md:h-[104px]">
            <div className="flex items-center gap-3 md:gap-6 lg:gap-8 min-w-0 flex-1">
              <div className="flex-shrink-0">
                <Link to="/" className="block">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/logo.png`}
                    alt="وزارة الشؤون الإسلامية والدعوة والإرشاد"
                    width={80}
                    height={80}
                    className="h-16 w-16 md:h-20 md:w-20 object-contain hover:opacity-80 transition-opacity"
                    onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = `${process.env.PUBLIC_URL}/favicon.ico`;
                      }}
                  />
                </Link>
              </div>

              {/* Mobile: hamburger button */}
              <button
                type="button"
                className="md:hidden flex items-center justify-center w-11 h-11 min-h-[44px] min-w-[44px] rounded-xl text-gray-700 hover:bg-gray-100 hover:text-sa-600 active:scale-95 transition-all duration-200"
                onClick={() => setOpenMobileMenu((p) => !p)}
                aria-expanded={openMobileMenu}
                aria-controls={mobileMenuId}
                aria-label={openMobileMenu ? (t("closeMenu") || "إغلاق القائمة") : (t("openMenu") || "فتح القائمة")}
              >
                {openMobileMenu ? (
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>

              <nav className="hidden md:flex items-center gap-3 lg:gap-4 min-w-0 flex-1" aria-label="القائمة الرئيسية">
                {/* Home */}
                <Link
                  to="/"
                  className={[navBase, isActivePath("/") ? `${navActive} ${navUnderline}` : navInactive].join(" ")}
                >
                  {tt("home", "الرئيسية")}
                </Link>

                {/* Mosque Search (dropdown) */}
                <div className="relative" ref={mosqueMenuRef}>
                  <button
                    type="button"
                    onClick={() => setOpenMosqueMenu((p) => !p)}
                    className={[navBase, isMosqueSearchActive ? `${navActive} ${navUnderline}` : navInactive].join(" ")}
                    aria-expanded={openMosqueMenu}
                    aria-haspopup="true"
                  >
                    <span>{tt("mosqueSearch", "البحث عن المساجد")}</span>
                    <HugeiconsIcon
                      icon={openMosqueMenu ? ArrowUp01Icon : ArrowDown01Icon}
                      size={20}
                      color="currentColor"
                      strokeWidth={1.5}
                    />
                  </button>

                  {openMosqueMenu && (
                    <div
                      className={[
                        "absolute mt-2 min-w-[320px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden",
                        isRTL ? "right-0" : "left-0",
                      ].join(" ")}
                    >
                      {/* Sub item 1: Mosque Search */}
                      <button
                        type="button"
                        onClick={() => {
                          setOpenMosqueMenu(false);
                          navigate("/service/mosque-search");
                        }}
                        className={[
                          "w-full flex items-center justify-between gap-4 px-5 py-4 transition-all hover:bg-gray-50",
                          isMosqueSearchPage ? "bg-sa-50" : "bg-white",
                        ].join(" ")}
                      >
                        <div className={["flex-1", submenuTextAlign].join(" ")}>
                          <div className="text-[15px] font-bold text-gray-900">{tt("mosqueSearch", "البحث عن المساجد")}</div>
                        </div>
                        <div className="h-10 w-10 rounded-2xl bg-gray-50 text-sa-700 flex items-center justify-center">
                          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="7" />
                            <path d="M21 21l-4.35-4.35" />
                          </svg>
                        </div>
                      </button>

                      <div className="h-px bg-gray-100" />

                      {/* Sub item 2: Mosque Map */}
                      <button
                        type="button"
                        onClick={() => {
                          setOpenMosqueMenu(false);
                          navigate(mosqueMapItem.to);
                        }}
                        className={[
                          "w-full flex items-center justify-between gap-4 px-5 py-4 transition-all hover:bg-gray-50",
                          isMosqueMapPage ? "bg-sa-50" : "bg-white",
                        ].join(" ")}
                      >
                        <div className={["flex-1", submenuTextAlign].join(" ")}>
                          <div className="text-[15px] font-bold text-gray-900">{mosqueMapItem.label}</div>
                        </div>

                        <div
                          className={[
                            "h-10 w-10 rounded-2xl flex items-center justify-center",
                            isMosqueMapPage ? "bg-sa-100 text-sa-700" : "bg-gray-50 text-sa-700",
                          ].join(" ")}
                        >
                          {mosqueMapItem.icon}
                        </div>
                      </button>
                    </div>
                  )}
                </div>

                {/* land */}
                {/* ✅ Land (dropdown) */}
                <div className="relative" ref={landMenuRef}>
                  <button
                    type="button"
                    onClick={() => setOpenLandMenu((p) => !p)}
                    className={[navBase, isLandActive ? `${navActive} ${navUnderline}` : navInactive].join(" ")}
                    aria-expanded={openLandMenu}
                    aria-haspopup="true"
                  >
                    <span>{tt("availableLandsForMosque", "الأراضي المتاحة لبناء مسجد")}</span>
                    <HugeiconsIcon
                      icon={openLandMenu ? ArrowUp01Icon : ArrowDown01Icon}
                      size={20}
                      color="currentColor"
                      strokeWidth={1.5}
                    />
                  </button>

                  {openLandMenu && (
                    <div
                      className={[
                        "absolute mt-2 min-w-[320px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden",
                        isRTL ? "right-0" : "left-0",
                      ].join(" ")}
                    >
                      {/* Item 1: Land service page */}
                      <button
                        type="button"
                        onClick={() => {
                          setOpenLandMenu(false);
                          navigate("/service/available-lands");
                        }}
                        className={[
                          "w-full flex items-center justify-between gap-4 px-5 py-4 transition-all hover:bg-gray-50",
                          isLandServicePage ? "bg-sa-50" : "bg-white",
                        ].join(" ")}
                      >
                        <div className={["flex-1", isRTL ? "text-right" : "text-left"].join(" ")}>
                          <div className="text-[15px] font-bold text-gray-900">
                            {tt("availableLandsForMosque", "الأراضي المتاحة لبناء مسجد")}
                          </div>
                        </div>
                        <div
                          className={[
                            "h-10 w-10 rounded-2xl flex items-center justify-center",
                            isLandServicePage ? "bg-sa-100 text-sa-700" : "bg-gray-50 text-sa-700",
                          ].join(" ")}
                        >
                          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="8" />
                            <path d="M8 12h8" />
                          </svg>
                        </div>
                      </button>



                      {/* Item 2: Land map page */}
                      <button
                        type="button"
                        onClick={() => {
                          setOpenLandMenu(false);
                          navigate("/benefaction/lands/map");
                        }}
                        className={[
                          "w-full flex items-center justify-between gap-4 px-5 py-4 transition-all hover:bg-gray-50",
                          isLandMapPage ? "bg-sa-50" : "bg-white",
                        ].join(" ")}
                      >
                        <div className={["flex-1", isRTL ? "text-right" : "text-left"].join(" ")}>
                          <div className="text-[15px] font-bold text-gray-900">
                            خريطة الأراضي
                          </div>
                        </div>
                        <div
                          className={[
                            "h-10 w-10 rounded-2xl flex items-center justify-center",
                            isLandMapPage ? "bg-sa-100 text-sa-700" : "bg-gray-50 text-sa-700",
                          ].join(" ")}
                        >
                          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3z" />
                            <path d="M9 3v15" />
                            <path d="M15 6v15" />
                          </svg>
                        </div>
                      </button>
                    </div>
                  )}
                </div>

                {/* ✅ Mosque Contribution (dropdown) */}
<div className="relative" ref={contributionMenuRef}>
  <button
    type="button"
    onClick={() => setOpenContributionMenu((p) => !p)}
    className={[navBase, isMosqueDonationActive ? `${navActive} ${navUnderline}` : navInactive].join(" ")}
    aria-expanded={openContributionMenu}
    aria-haspopup="true"
  >
    <span>{tt("serviceMosqueContribution", "فرص عقود الصيانة")}</span>
    <HugeiconsIcon
      icon={openContributionMenu ? ArrowUp01Icon : ArrowDown01Icon}
      size={20}
      color="currentColor"
      strokeWidth={1.5}
    />
  </button>

  {openContributionMenu && (
    <div
      className={[
        "absolute mt-2 min-w-[320px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden",
        isRTL ? "right-0" : "left-0",
      ].join(" ")}
    >
      {/* Item 1: Service page (appears before opportunities list) */}
      <button
        type="button"
        onClick={() => {
          setOpenContributionMenu(false);
          navigate("/service/mosque-contribution");
        }}
        className={[
          "w-full flex items-center justify-between gap-4 px-5 py-4 transition-all hover:bg-gray-50",
          isServiceContributionPage ? "bg-sa-50" : "bg-white",
        ].join(" ")}
      >
        <div className={["flex-1", isRTL ? "text-right" : "text-left"].join(" ")}>
          <div className="text-[15px] font-bold text-gray-900">
            {tt("mosqueContributionServiceTitle", "صفحة الخدمة")}
          </div>
        </div>
        <div className="h-10 w-10 rounded-2xl bg-gray-50 text-sa-700 flex items-center justify-center">
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="8" />
            <path d="M8 12h8" />
          </svg>
        </div>
      </button>

      <div className="h-px bg-gray-100" />

      {/* Item 2: Opportunities Map */}
      <button
        type="button"
        onClick={() => {
          setOpenContributionMenu(false);
          navigate("/mosque/donation-opportunities-map");
        }}
        className={[
          "w-full flex items-center justify-between gap-4 px-5 py-4 transition-all hover:bg-gray-50",
          isDonationOpportunitiesMapPage ? "bg-sa-50" : "bg-white",
        ].join(" ")}
      >
        <div className={["flex-1", isRTL ? "text-right" : "text-left"].join(" ")}>
          <div className="text-[15px] font-bold text-gray-900">
            {tt("donationOpportunitiesMap", "خريطة فرص المساهمة في النظافة والصيانة")}
          </div>
        </div>
        <div
          className={[
            "h-10 w-10 rounded-2xl flex items-center justify-center",
            isDonationOpportunitiesMapPage ? "bg-sa-100 text-sa-700" : "bg-gray-50 text-sa-700",
          ].join(" ")}
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3z" />
            <path d="M9 3v15" />
            <path d="M15 6v15" />
          </svg>
        </div>
      </button>
    </div>
  )}
</div>

              </nav>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                type="button"
                id="header-search-trigger"
                aria-expanded={searchPanelOpen}
                aria-controls="site-search-panel"
                onClick={() => setSearchPanelOpen((o) => !o)}
                className={[
                  navBase,
                  searchPanelOpen ? `${navActive} ${navUnderline}` : navInactive,
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-sa-500 focus-visible:ring-offset-2",
                ].join(" ")}
                aria-label={tt("headerSearchLabel", "البحث")}
              >
                <svg
                  className="h-4 w-4 shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <span className="hidden sm:inline">{tt("headerSearchLabel", "البحث")}</span>
              </button>

              <button
                type="button"
                onClick={toggleLanguage}
                className="px-3 py-2 text-gray-700 hover:text-sa-600 hover:bg-gray-100 rounded-lg transition-colors font-semibold text-sm lg:text-base"
                aria-label={language === "ar" ? "Switch to English" : "التبديل إلى العربية"}
              >
                {language === "ar" ? tt("english", "English") : tt("arabic", "العربية")}
              </button>

              {/* تسجيل الدخول / حسابي (طلباتي + تسجيل الخروج) */}
              {!loggedIn ? (
                <Link
                  to="/benefaction/login"
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-sa-600 hover:bg-gray-100 rounded-lg transition-colors font-semibold text-sm lg:text-base"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <span>{t("footerLogin") || "تسجيل الدخول"}</span>
                </Link>
              ) : (
                <div className="relative" ref={userMenuRef}>
                  <button
                    type="button"
                    onClick={() => setOpenUserMenu((p) => !p)}
                    className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-sa-600 hover:bg-gray-100 rounded-lg transition-colors font-semibold text-sm lg:text-base"
                    aria-expanded={openUserMenu}
                    aria-haspopup="true"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    <span>حسابي</span>
                    <HugeiconsIcon
                      icon={openUserMenu ? ArrowUp01Icon : ArrowDown01Icon}
                      size={20}
                      color="currentColor"
                      strokeWidth={1.5}
                    />
                  </button>
                  {openUserMenu && (
                    <div
                      className={[
                        "absolute mt-2 min-w-[180px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden py-2",
                        isRTL ? "left-0" : "right-0",
                      ].join(" ")}
                      role="menu"
                    >
                      <Link
                        to="/my-profile"
                        className="flex items-center gap-2 w-full px-4 py-3 text-right text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                        role="menuitem"
                        onClick={() => setOpenUserMenu(false)}
                      >
                        الملف التعريفي
                      </Link>
                      <Link
                        to="/my-dashboard"
                        className="flex items-center gap-2 w-full px-4 py-3 text-right text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                        role="menuitem"
                        onClick={() => setOpenUserMenu(false)}
                      >
                        لوحة المؤشرات
                      </Link>
                      <Link
                        to="/my-requests"
                        className="flex items-center gap-2 w-full px-4 py-3 text-right text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                        role="menuitem"
                        onClick={() => setOpenUserMenu(false)}
                      >
                        طلباتي
                      </Link>
                      <button
                        type="button"
                        className="flex items-center gap-2 w-full px-4 py-3 text-right text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
                        role="menuitem"
                        onClick={handleLogout}
                      >
                        تسجيل الخروج
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {searchPanelOpen && (
        <div
          id="site-search-panel"
          role="search"
          aria-labelledby="site-search-heading"
          className="w-full border-b border-gray-200 bg-white shadow-sm"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
            <div
              className="flex items-center justify-between gap-4 mb-6"
              dir={isRTL ? "rtl" : "ltr"}
            >
              <h2
                id="site-search-heading"
                className={`text-2xl md:text-3xl font-bold text-gray-900 flex-1 min-w-0 ${isRTL ? "text-right" : "text-left"}`}
              >
                {tt("search_box.title", "البحث")}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setSearchPanelOpen(false);
                  window.requestAnimationFrame(() => {
                    document.getElementById("header-search-trigger")?.focus();
                  });
                }}
                className="shrink-0 flex h-11 w-11 min-h-[44px] min-w-[44px] items-center justify-center rounded-xl text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-sa-500 transition-colors"
                aria-label={tt("closeSearch", "إغلاق البحث")}
              >
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
            <form
              className="flex flex-col sm:flex-row gap-3 sm:items-stretch"
              dir={isRTL ? "rtl" : "ltr"}
              onSubmit={(e) => {
                e.preventDefault();
                const q = searchQuery.trim();
                setSearchPanelOpen(false);
                if (q) {
                  navigate(`/search?q=${encodeURIComponent(q)}`);
                } else {
                  navigate("/services");
                }
              }}
            >
              <div className="flex-1 min-w-0">
                <DgaSearchBox
                  ref={searchInputRef}
                  name="q"
                  placeholder={tt("search_box.search", "بحث")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  dir={isRTL ? "rtl" : "ltr"}
                  micAriaLabel={language === "ar" ? "البحث الصوتي" : "Voice search"}
                />
              </div>
              <button
                type="submit"
                className="shrink-0 inline-flex items-center justify-center px-6 py-3 rounded-lg bg-sa-600 text-white font-semibold shadow-sm hover:bg-sa-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sa-500 min-h-[48px]"
              >
                {tt("search_box.submit", "بحث")}
              </button>
            </form>
            <div className={`mt-8 ${isRTL ? "text-right" : "text-left"}`}>
              <p className="text-lg font-semibold text-sa-700 mb-3">{tt("search_box.suggestions", "اقتراحات")}</p>
              <ul className="flex flex-wrap gap-2">
                <li>
                  <Link
                    to="/service/mosque-search"
                    onClick={() => setSearchPanelOpen(false)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-200 bg-gray-50 text-gray-800 font-medium hover:bg-gray-100 transition-colors"
                  >
                    <span className="text-sa-600" aria-hidden>
                      +
                    </span>
                    {tt("mosqueSearch", "البحث عن المساجد")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/service/available-lands"
                    onClick={() => setSearchPanelOpen(false)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-200 bg-gray-50 text-gray-800 font-medium hover:bg-gray-100 transition-colors"
                  >
                    <span className="text-sa-600" aria-hidden>
                      +
                    </span>
                    {tt("availableLandsForMosque", "الأراضي المتاحة لبناء مسجد")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/service/mosque-contribution"
                    onClick={() => setSearchPanelOpen(false)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-200 bg-gray-50 text-gray-800 font-medium hover:bg-gray-100 transition-colors"
                  >
                    <span className="text-sa-600" aria-hidden>
                      +
                    </span>
                    {tt("serviceMosqueContribution", "فرص عقود الصيانة")}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Mobile menu: overlay + slide-in panel */}
      {openMobileMenu && (
        <>
          <div
            className="fixed inset-0 z-[999] bg-black/30 md:hidden"
            aria-hidden="true"
            onClick={() => setOpenMobileMenu(false)}
          />
          <div
            id={mobileMenuId}
            role="dialog"
            aria-modal="true"
            aria-label="القائمة الرئيسية"
            className={[
              "fixed top-0 z-[1000] w-full max-w-[320px] h-full bg-white shadow-2xl overflow-y-auto md:hidden",
              "flex flex-col",
              isRTL ? "right-0" : "left-0",
            ].join(" ")}
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between flex-shrink-0">
              <span className="text-lg font-bold text-gray-900">القائمة الرئيسية</span>
              <button
                type="button"
                className="w-11 h-11 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl text-gray-600 hover:bg-gray-100 hover:text-gray-900 active:scale-95 transition-all duration-200"
                onClick={() => setOpenMobileMenu(false)}
                aria-label={t("closeMenu") || "إغلاق القائمة"}
              >
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
            <nav className="p-4 space-y-1" dir={isRTL ? "rtl" : "ltr"}>
              <Link
                to="/"
                className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl text-base font-semibold min-h-[44px] transition-colors duration-200 ${isActivePath("/") ? "bg-sa-100 text-sa-800" : "text-gray-800 hover:bg-sa-50 hover:text-sa-800"}`}
                onClick={() => setOpenMobileMenu(false)}
              >
                {tt("home", "الرئيسية")}
              </Link>

              <div className="pt-2">
                <div className="px-4 py-2 text-sm font-medium text-gray-500">{tt("mosqueSearch", "البحث عن المساجد")}</div>
                <button
                  type="button"
                  onClick={() => { setOpenMobileMenu(false); navigate("/service/mosque-search"); }}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl text-base font-medium min-h-[44px] text-right transition-colors duration-200 ${isMosqueSearchPage ? "bg-sa-50 text-sa-800" : "text-gray-800 hover:bg-sa-50 hover:text-sa-800"}`}
                >
                  {tt("mosqueSearch", "البحث عن المساجد")}
                </button>
                <button
                  type="button"
                  onClick={() => { setOpenMobileMenu(false); navigate(mosqueMapItem.to); }}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl text-base font-medium min-h-[44px] text-right transition-colors duration-200 ${isMosqueMapPage ? "bg-sa-50 text-sa-800" : "text-gray-800 hover:bg-sa-50 hover:text-sa-800"}`}
                >
                  {mosqueMapItem.label}
                </button>
              </div>


                            <div className="pt-2">
                <div className="px-4 py-2 text-sm font-medium text-gray-500">
                  {tt("availableLandsForMosque", "الأراضي المتاحة لبناء مسجد")}
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setOpenMobileMenu(false);
                    navigate("/service/available-lands");
                  }}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl text-base font-medium min-h-[44px] text-right transition-colors duration-200 ${
                    isLandServicePage ? "bg-sa-50 text-sa-800" : "text-gray-800 hover:bg-sa-50 hover:text-sa-800"
                  }`}
                >
                  {tt("availableLandsForMosque", "الأراضي المتاحة لبناء مسجد")}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setOpenMobileMenu(false);
                    navigate("/benefaction/lands");
                  }}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl text-base font-medium min-h-[44px] text-right transition-colors duration-200 ${
                    isLandListPage ? "bg-sa-50 text-sa-800" : "text-gray-800 hover:bg-sa-50 hover:text-sa-800"
                  }`}
                >
                  عرض الأراضي
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setOpenMobileMenu(false);
                    navigate("/benefaction/lands/map");
                  }}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl text-base font-medium min-h-[44px] text-right transition-colors duration-200 ${
                    isLandMapPage ? "bg-sa-50 text-sa-800" : "text-gray-800 hover:bg-sa-50 hover:text-sa-800"
                  }`}
                >
                  خريطة الأراضي
                </button>
              </div>

              <div className="pt-2">
                <div className="px-4 py-2 text-sm font-medium text-gray-500">{tt("serviceMosqueContribution", "فرص عقود الصيانة")}</div>
                <button
                  type="button"
                  onClick={() => { setOpenMobileMenu(false); navigate("/service/mosque-contribution"); }}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl text-base font-medium min-h-[44px] text-right transition-colors duration-200 ${isServiceContributionPage ? "bg-sa-50 text-sa-800" : "text-gray-800 hover:bg-sa-50 hover:text-sa-800"}`}
                >
                  {tt("mosqueContributionServiceTitle", "صفحة الخدمة")}
                </button>
                <button
                  type="button"
                  onClick={() => { setOpenMobileMenu(false); navigate("/mosque/donation-opportunities-map"); }}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl text-base font-medium min-h-[44px] text-right transition-colors duration-200 ${isDonationOpportunitiesMapPage ? "bg-sa-50 text-sa-800" : "text-gray-800 hover:bg-sa-50 hover:text-sa-800"}`}
                >
                  {tt("donationOpportunitiesMap", "خريطة فرص المساهمة في النظافة والصيانة")}
                </button>
              </div>
            </nav>
          </div>
        </>
      )}
    </div>
  );
};

export default SimpleHeader;
